import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import HighlightOffOutlined from '@mui/icons-material/HighlightOffOutlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import { DataTable, EmptyState, LoadingButton, Notice, PageHeader, StatusChip, type DataTableColumn, type StatusTone } from '@routes-plan/ui';
import StandardModal from '../../../components/modales/StandardModal';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { setPageTitle } from '../../../utils/pageTitle';
import { formatCurrency } from '../../../utils/format/currency';
import { formatDateTime } from '../../admin/devices/DeviceList';
import { useT, type TextKey } from '../../../i18n';
import RowAction from '../components/RowAction';
import { pageSx } from '../components/layout';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';

/**
 * Préstamos → Aprobar correcciones: las correcciones y los borrados de abonos que pidió otra persona.
 * Quien pidió no puede aprobar (el API lo impide). Cada usuario ve solo las rutas de sus sedes.
 */
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type Status = 'pending' | 'applied' | 'rejected' | 'cancelled' | 'failed';
type Filter = 'pending' | 'applied' | 'rejected' | 'all';

interface Snapshot {
  value: number;
  discountValue: number;
  paymentDate: string | null;
  cash: boolean;
}

interface CorrectionRequest {
  id: number;
  payId: number;
  kind: 'update' | 'delete';
  status: Status;
  before: Snapshot;
  changes: Partial<Snapshot> & Record<string, unknown> | null;
  reason: string | null;
  requestedFrom: 'web' | 'app';
  requestedAt: string;
  requestedBy: { id: number; username: string | null };
  reviewedBy: { id: number; username: string | null } | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  error: string | null;
  route: { id: number; name: string | null } | null;
  client: string | null;
}

const STATUS_TONE: Record<Status, StatusTone> = {
  pending: 'warning',
  applied: 'success',
  rejected: 'neutral',
  cancelled: 'neutral',
  failed: 'danger',
};

/** «20.000 → 15.000» y demás cambios de la corrección, en una línea. */
export const describeChange = (r: CorrectionRequest, t: (k: TextKey, v?: Record<string, string | number>) => string) => {
  if (r.kind === 'delete') return t('correctionApprovals.deleteOf', { value: formatCurrency(Number(r.before.value)) });
  const c = r.changes ?? {};
  const parts: string[] = [];
  if (c.value !== undefined && Number(c.value) !== Number(r.before.value)) {
    parts.push(`${t('correctionApprovals.value')}: ${formatCurrency(Number(r.before.value))} → ${formatCurrency(Number(c.value))}`);
  }
  if (c.discountValue !== undefined && Number(c.discountValue) !== Number(r.before.discountValue)) {
    parts.push(`${t('correctionApprovals.discount')}: ${formatCurrency(Number(r.before.discountValue))} → ${formatCurrency(Number(c.discountValue))}`);
  }
  if (c.paymentDate !== undefined && String(c.paymentDate).slice(0, 10) !== String(r.before.paymentDate ?? '')) {
    parts.push(`${t('correctionApprovals.date')}: ${r.before.paymentDate ?? '—'} → ${String(c.paymentDate).slice(0, 10)}`);
  }
  if (c.cash !== undefined && Boolean(c.cash) !== Boolean(r.before.cash)) {
    parts.push(c.cash ? t('correctionApprovals.toCash') : t('correctionApprovals.toBank'));
  }
  return parts.join(' · ') || t('correctionApprovals.otherChanges');
};

const CorrectionApprovals: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  setPageTitle('menu.loans.correction-approvals');
  const t = useT();
  const { canRead, canUpdate } = useResourcePermissions('/loans/correction-approvals');
  const [filter, setFilter] = useState<Filter>('pending');
  const [rows, setRows] = useState<CorrectionRequest[]>([]);
  const [pending, setPending] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [dialog, setDialog] = useState<{ action: 'approve' | 'reject'; request: CorrectionRequest } | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    showLoading();
    try {
      const { data } = await axios.get<{ data: CorrectionRequest[]; pending: number }>('payment-corrections', {
        params: filter === 'all' ? {} : { status: filter },
      });
      setRows(data.data);
      setPending(data.pending);
    } catch {
      showAlert(t('correctionApprovals.loadError'), 'error');
    } finally {
      setLoaded(true);
      hideLoading();
    }
  }, [filter, hideLoading, showAlert, showLoading, t]);

  useEffect(() => {
    if (canRead) void load();
  }, [canRead, load]);

  const confirm = async () => {
    if (!dialog) return;
    setSaving(true);
    try {
      await axios.post(`payment-corrections/${dialog.request.id}/${dialog.action}`, { note });
      showAlert(dialog.action === 'approve' ? t('correctionApprovals.approvedOk') : t('correctionApprovals.rejectedOk'), 'success');
      setDialog(null);
      await load();
    } catch (error) {
      const code = axios.isAxiosError(error) ? (error.response?.data as { code?: string })?.code : undefined;
      const key: TextKey =
        code === 'SAME_PERSON'
          ? 'correctionApprovals.samePerson'
          : code === 'PAY_CHANGED'
            ? 'correctionApprovals.payChanged'
            : code === 'EXCEEDS_BALANCE'
              ? 'correctionApprovals.exceedsBalance'
              : code === 'PAY_GONE'
                ? 'correctionApprovals.payGone'
                : 'correctionApprovals.actionError';
      showAlert(t(key), 'error');
      setDialog(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const tableRows = rows.map((r) => ({
    id: r.id,
    request: r,
    client: r.client ?? `#${r.payId}`,
    status: t(`correctionApprovals.status.${r.status}` as TextKey),
    kind: r.kind === 'delete' ? t('correctionApprovals.kindDelete') : t('correctionApprovals.kindUpdate'),
    change: describeChange(r, t),
    reason: r.reason ?? '',
    requester: `${r.requestedBy.username ?? '—'} · ${r.requestedFrom === 'app' ? t('correctionApprovals.fromApp') : t('correctionApprovals.fromWeb')}`,
    route: r.route?.name ?? '—',
    requestedAt: formatDateTime(r.requestedAt),
    review: r.reviewedBy
      ? [r.reviewedBy.username, formatDateTime(r.reviewedAt), r.reviewNote, r.error].filter(Boolean).join(' · ')
      : '',
  }));

  type ApprovalRow = (typeof tableRows)[number];
  const columns: DataTableColumn<ApprovalRow>[] = [
    { id: 'client', header: t('correctionApprovals.colClient'), value: (row) => row.client },
    {
      id: 'status',
      header: t('correctionApprovals.colStatus'),
      value: (row) => row.status,
      render: (row) => <StatusChip label={row.status} tone={STATUS_TONE[row.request.status]} />,
    },
    { id: 'kind', header: t('correctionApprovals.colKind'), value: (row) => row.kind },
    { id: 'change', header: t('correctionApprovals.colChange'), value: (row) => row.change, wrap: true, width: 260 },
    { id: 'reason', header: t('correctionApprovals.colReason'), value: (row) => row.reason, wrap: true, width: 200, hideBelow: 'md' },
    { id: 'requester', header: t('correctionApprovals.colRequestedBy'), value: (row) => row.requester, hideBelow: 'md' },
    { id: 'route', header: t('correctionApprovals.colRoute'), value: (row) => row.route, hideBelow: 'sm' },
    { id: 'requestedAt', header: t('correctionApprovals.colRequestedAt'), value: (row) => row.requestedAt, hideBelow: 'sm' },
    { id: 'review', header: t('correctionApprovals.colReview'), value: (row) => row.review, wrap: true, width: 220, hideBelow: 'md' },
  ];

  if (!canRead && loaded) {
    return (
      <Box sx={pageSx}>
        <PageHeader title={t('correctionApprovals.title')} />
        <Notice severity="warning">{t('correctionApprovals.noReadPermission')}</Notice>
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <PageHeader
        title={t('correctionApprovals.title')}
        description={t('correctionApprovals.intro')}
        helpAction={<ScreenTourButton />}
        actions={
          <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={() => void load()} data-tour="correction-refresh">
            {t('common.refresh')}
          </Button>
        }
      />
      <Tabs
        data-tour="correction-tabs"
        value={filter}
        onChange={(_, v) => setFilter(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider', minWidth: 0 }}
      >
        <Tab value="pending" label={`${t('correctionApprovals.tabPending')} (${pending})`} />
        <Tab value="applied" label={t('correctionApprovals.tabApplied')} />
        <Tab value="rejected" label={t('correctionApprovals.tabRejected')} />
        <Tab value="all" label={t('correctionApprovals.tabAll')} />
      </Tabs>
      <DataTable<ApprovalRow>
        columns={columns}
        rows={tableRows}
        getRowId={(row) => row.id}
        loading={!loaded}
        searchable
        exportFileName={t('correctionApprovals.exportFile')}
        ariaLabel={t('correctionApprovals.title')}
        tourId="correction-approvals"
        rowActions={(row) =>
          row.request.status !== 'pending' ? null : (
            <>
              <RowAction
                label={`${t('correctionApprovals.approve')} ${row.request.client ?? row.request.payId}`}
                icon={<CheckCircleOutlineOutlined fontSize="small" />}
                color="success"
                dataTour="correction-approve"
                allowed={canUpdate}
                deniedLabel={t('correctionApprovals.noPermission')}
                onClick={() => {
                  setNote('');
                  setDialog({ action: 'approve', request: row.request });
                }}
              />
              <RowAction
                label={`${t('correctionApprovals.reject')} ${row.request.client ?? row.request.payId}`}
                icon={<HighlightOffOutlined fontSize="small" />}
                color="error"
                dataTour="correction-reject"
                allowed={canUpdate}
                deniedLabel={t('correctionApprovals.noPermission')}
                onClick={() => {
                  setNote('');
                  setDialog({ action: 'reject', request: row.request });
                }}
              />
            </>
          )
        }
        emptyState={
          <EmptyState
            dense
            icon={<FactCheckOutlined />}
            title={filter === 'pending' ? t('correctionApprovals.emptyPending') : t('correctionApprovals.empty')}
          />
        }
      />

      {dialog && (
        <StandardModal
          open
          onClose={() => !saving && setDialog(null)}
          title={dialog.action === 'approve' ? t('correctionApprovals.approveTitle') : t('correctionApprovals.rejectTitle')}
          subtitle={`${dialog.request.client ?? `#${dialog.request.payId}`} · ${describeChange(dialog.request, t)}`}
          maxWidth={520}
          disableClose={saving}
          actions={
            <>
              <Button variant="outlined" onClick={() => setDialog(null)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <LoadingButton variant="contained" color={dialog.action === 'approve' ? 'success' : 'error'} onClick={() => void confirm()} loading={saving}>
                {dialog.action === 'approve' ? t('correctionApprovals.approve') : t('correctionApprovals.reject')}
              </LoadingButton>
            </>
          }
        >
          <Stack spacing={2}>
            <Typography variant="body2">
              {dialog.action === 'approve' ? t('correctionApprovals.approveBody') : t('correctionApprovals.rejectBody')}
            </Typography>
            {dialog.request.reason && (
              <Alert severity="info">{t('correctionApprovals.reasonGiven', { reason: dialog.request.reason })}</Alert>
            )}
            <TextField
              label={t('correctionApprovals.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 300 }}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </StandardModal>
      )}
    </Box>
  );
};

export default CorrectionApprovals;
